import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { InteriorSlide, SlideHeader, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const QUARTERS = [{
  quarter: 'Q1',
  theme: 'Establish relevance',
  months: [{
    month: 'Jan',
    message: 'Guideline refresh quiz'
  }, {
    month: 'Feb',
    message: 'Dosing confidence poll'
  }, {
    month: 'Mar',
    message: 'Case: paediatric dosing'
  }],
  learning: 'Dosing questions drive 2× completion'
}, {
  quarter: 'Q2',
  theme: 'Double down on dosing',
  months: [{
    month: 'Apr',
    message: 'Dosing decision tree'
  }, {
    month: 'May',
    message: 'Peer comparison: choices'
  }, {
    month: 'Jun',
    message: 'Resistance & stewardship'
  }],
  learning: 'Peer comparison lifts repeat opens'
}, {
  quarter: 'Q3',
  theme: 'Broaden the topic set',
  months: [{
    month: 'Jul',
    message: 'Adherence conversation tips'
  }, {
    month: 'Aug',
    message: 'Patient-type selector'
  }, {
    month: 'Sep',
    message: 'Combination therapy myths'
  }],
  learning: 'Short teasers outperform long formats'
}, {
  quarter: 'Q4',
  theme: 'Repeat the winners',
  months: [{
    month: 'Oct',
    message: 'Best-of teaser rerun'
  }, {
    month: 'Nov',
    message: 'Seasonal respiratory playbook'
  }, {
    month: 'Dec',
    message: 'Year in review & next year'
  }],
  learning: 'Winning formats become next year’s masters'
}];
export function SlideCampaignCalendar({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner eyebrow="Key Message" data-id="element-377" data-mp="14jfd1ekxyupt">
          The calendar is never fixed. Each month's behavioural data decides what
          the next month says.
        </SlideBanner>} data-id="element-376" data-mp="14k6wjr19l1zq">
      <SlideHeader eyebrow="Campaign Calendar" data-id="element-378" data-mp="3p8bguefnl4uk">
        Different messages over time, each one shaped by what we learned
      </SlideHeader>

      <div className="flex-1 min-h-0 flex flex-col max-w-7xl mx-auto w-full" data-id="element-379" data-mp="3a6v3vznaxvbs">
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-4 gap-3 lg:gap-4" data-id="element-380" data-mp="3a8z5gaxqai8w">
          {QUARTERS.map((q, i) => <motion.div key={q.quarter} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3 + i * 0.15,
          duration: 0.5
        }} className="relative flex flex-col rounded-2xl border border-black/5 bg-white p-4 shadow-sm" data-id="element-381" data-mp="31eb5n0ax9srh" data-mpi={i}>
              {/* Learning flows forward */}
              {i < QUARTERS.length - 1 && <span className="absolute top-1/2 -right-3 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-md lg:flex" data-id="element-382" data-mp="2diow53v32mta">
                  <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-383" data-mp="bq0omibepd7p" />
                </span>}

              <div className="flex items-baseline gap-2 mb-3" data-id="element-384" data-mp="j88jty7lo9at">
                <span className="rounded-full bg-[var(--brand)] px-2.5 py-0.5 text-xs font-bold text-white" data-id="element-385" data-mp="o731gwttzrtc">
                  {q.quarter}
                </span>
                <h3 className="text-[var(--fg)] font-bold text-sm leading-tight" data-id="element-386" data-mp="t0xlk1dcm8sy">
                  {q.theme}
                </h3>
              </div>

              <ul className="flex-1 space-y-2" data-id="element-387" data-mp="axlcftsljwf">
                {q.months.map((m, mi) => <motion.li key={m.month} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.45 + i * 0.15 + mi * 0.07,
              duration: 0.4
            }} className="flex items-center gap-2.5 rounded-xl bg-[var(--surface)] px-3 py-2" data-id="element-388" data-mp="5a1uj6gpvhmr" data-mpi={mi}>
                    <span className="text-[0.65rem] font-bold uppercase tracking-wider text-[var(--brand)] w-7 flex-shrink-0" data-id="element-389" data-mp="1j8tyg8oduoth">
                      {m.month}
                    </span>
                    <span className="text-[13px] font-medium leading-snug text-[var(--fg)]" data-id="element-390" data-mp="1t6rkcpirs9ix">
                      {m.message}
                    </span>
                  </motion.li>)}
              </ul>

              <div className="mt-3 flex items-start gap-2 border-t border-black/5 pt-3" data-id="element-391" data-mp="227zh335n12aw">
                <SparklesIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--accent)]" data-id="element-392" data-mp="2gw2q6lez8u3x" />
                <div data-id="element-393" data-mp="2bxj2afgtp88x">
                  <p className="text-[0.6rem] font-bold uppercase tracking-wider text-[var(--muted)]" data-id="element-394" data-mp="2bwzj6vffsdtb">
                    What we learned
                  </p>
                  <p className="text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-395" data-mp="2puo6sb0w1z22">
                    {q.learning}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 0.5
      }} className="mt-4 flex items-center gap-3 flex-shrink-0" data-id="element-396" data-mp="39j2elkqzabsu">
          <span className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-[var(--muted)] flex-shrink-0" data-id="element-397" data-mp="2lsq17ucyrmuv">
            Learning loop
          </span>
          <span className="h-0.5 flex-1 rounded-full bg-[var(--brand)]/25" data-id="element-398" data-mp="2qm145kennjhi" />
          <span className="text-[0.65rem] font-semibold text-[var(--brand)] flex-shrink-0" data-id="element-399" data-mp="2cu4k83n54fw7">
            Every cycle sharpens the message
          </span>
        </motion.div>
      </div>
    </InteriorSlide>;
}